import { ApiError, api, can, refusalReason } from '../lib/api'
import { flags, useFlag } from '../lib/flags'
import { resendSaid } from '../lib/mail'
import { myWorkChanged } from '../lib/myWork'
import { TASK_VERB } from '../lib/status'
import { useToast } from './ui'

// The one reminder control (release 3.2, removal item 1): it sends the vendor
// the questionnaire's invitation again.
//
// # WHY THERE IS ONLY ONE
//
// Sending the invitation again was written four times, with four labels
// ("Chase questionnaire", "Resend", "Resend the invitation", "Resend") and four
// copies of the same call, on the vendor header, the overview panel, the risk
// part and the Assessments drawer, while the vendor page's own next step,
// "Chase the late questionnaire", opened a page where it was impossible. Now
// there is this button, labelled TASK_VERB.chase ("Send reminder") wherever it
// is drawn: on the vendor's next step for every questionnaire sent or in
// progress (the main action once it is late, a quiet one before), on an
// overdue questionnaire's row in My work, and in the Assessments list's drawer
// for a questionnaire sent or in progress (release 3.3, O1). Every other
// screen links to the vendor.
//
// # ONE PRESS, ONE CALL
//
// A second press before the first answers would send the vendor the invitation
// twice. The guard is the set of questionnaires with a reminder in flight, not
// a button's own state, for two reasons. Two clicks can land in the same frame
// before React has drawn the disabled button. And the button is drawn in more
// than one place for the same questionnaire: on the vendor's line and under it
// once Details is open, and on its row in My work. A guard each button kept for
// itself let the second one send again (review of 11 Oct 2026).
//
// The API resends only a questionnaire the vendor has not handed back (sent or
// in progress); the caller decides that and offers the button only then.

const sending = flags()

// Runs send unless a reminder for this questionnaire is already in flight. A
// call made meanwhile is dropped, not queued, and answers false.
export async function sendOnce(assessmentId: string, send: () => Promise<void>): Promise<boolean> {
  if (sending.has(assessmentId)) return false
  sending.set(assessmentId, true)
  try {
    await send()
  } finally {
    sending.set(assessmentId, false)
  }
  return true
}

export function ChaseButton({
  assessmentId,
  primary,
  actionId,
  onChased,
}: {
  assessmentId: string
  primary?: boolean
  // The persona walk's id for the placement (data-action).
  actionId: string
  onChased?: () => void
}) {
  // True on every button for this questionnaire while one of them is sending.
  const busy = useFlag(sending, assessmentId)
  const toast = useToast()
  const allowed = can('questionnaire.issue')

  const chase = () =>
    sendOnce(assessmentId, async () => {
      if (!allowed) return
      try {
        const r = await api.resendInvitation(assessmentId)
        // Sent again only when mail will go. See lib/mail.ts.
        const said = resendSaid(r)
        toast(said.text, said.tone)
        myWorkChanged()
        onChased?.()
      } catch (err) {
        toast(err instanceof ApiError ? err.message : String(err), 'warn')
      }
    })

  return (
    <button
      type="button"
      className={primary ? 'primary' : undefined}
      disabled={busy || !allowed}
      title={allowed ? 'Send the vendor the invitation again, with the same link and due date' : refusalReason('questionnaire.issue')}
      onClick={() => void chase()}
      data-action={actionId}
    >
      {busy ? 'Sending' : TASK_VERB.chase}
    </button>
  )
}
